import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { ConfirmActions } from "src/redux/Confirm/actions";

/**
 * Guards a page's unsaved changes: while `dirty` is true, in-app
 * navigation opens the standard "Unsaved Changes" dialog (Discard
 * continues to the blocked destination, Stay cancels), and closing or
 * reloading the browser tab triggers the native leave prompt.
 * `summary` lists the pending changes, one per line.
 */
export default function useUnsavedChangesGuard(dirty, summary) {
    const dispatch = useDispatch();
    const history = useHistory();
    const allowRef = useRef(false);

    useEffect(() => {
        if (!dirty) return undefined;
        allowRef.current = false;

        const unblock = history.block((location) => {
            if (allowRef.current) return undefined;
            dispatch(ConfirmActions.Open({
                title: "Unsaved Changes",
                message: "The following changes will be lost:\n" + (summary || "(no changes)"),
                okTitle: "Discard",
                cancelTitle: "Stay",
                warning: true,
                onOk: () => {
                    allowRef.current = true;
                    unblock();
                    history.push(location.pathname + (location.search || ""));
                },
            }));
            return false;
        });

        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            unblock();
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [dirty, summary, dispatch, history]);
}
